import axios, { AxiosError } from "axios";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "react-query";
import { HttpClientError } from "../../../../providers/HttpClient";
import {
  SubmissionReviewRequest,
  SupervisorClockSubmissionDetail,
  SupervisorClockSubmissionSummary,
} from "../model/SupervisorClockSubmission";
import { invalidateAttendanceViews } from "./attendanceCache";

const ListPendingSubmissionsApiService = async (
  locationId: string
): Promise<SupervisorClockSubmissionSummary[]> => {
  try {
    const { data } = await axios.get("/supervisor-clock-submissions/", {
      params: { shift_location: locationId, status: "pending" },
    });
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

const GetSubmissionDetailApiService = async (
  submissionId: string
): Promise<SupervisorClockSubmissionDetail> => {
  try {
    const { data } = await axios.get(
      `/supervisor-clock-submissions/${submissionId}/`
    );
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

const ReviewSubmissionApiService = async (
  submissionId: string,
  request: SubmissionReviewRequest
): Promise<SupervisorClockSubmissionDetail> => {
  try {
    const { data } = await axios.post(
      `/supervisor-clock-submissions/${submissionId}/review/`,
      request
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      HttpClientError(error);
      throw error;
    }
    throw error;
  }
};

const useListPendingSubmissions = (
  locationId: string | undefined,
  enabled = true
): UseQueryResult<SupervisorClockSubmissionSummary[], Error | AxiosError> =>
  useQuery<SupervisorClockSubmissionSummary[], Error | AxiosError>(
    ["SupervisorClockSubmissions", locationId],
    () => ListPendingSubmissionsApiService(locationId as string),
    {
      enabled: enabled && !!locationId,
      keepPreviousData: true,
    }
  );

const useGetSubmissionDetail = (
  submissionId: string | null
): UseQueryResult<SupervisorClockSubmissionDetail, Error | AxiosError> =>
  useQuery<SupervisorClockSubmissionDetail, Error | AxiosError>(
    ["SupervisorClockSubmissionDetail", submissionId],
    () => GetSubmissionDetailApiService(submissionId as string),
    {
      enabled: !!submissionId,
    }
  );

const useReviewSubmission = (
  locationId: string | undefined
): ReturnType<
  typeof useMutation<
    SupervisorClockSubmissionDetail,
    AxiosError,
    { submissionId: string; request: SubmissionReviewRequest }
  >
> => {
  const queryClient = useQueryClient();
  return useMutation<
    SupervisorClockSubmissionDetail,
    AxiosError,
    { submissionId: string; request: SubmissionReviewRequest }
  >(
    ({ submissionId, request }) =>
      ReviewSubmissionApiService(submissionId, request),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "SupervisorClockSubmissions",
          locationId,
        ]);
        invalidateAttendanceViews(queryClient);
      },
    }
  );
};

export {
  ListPendingSubmissionsApiService,
  GetSubmissionDetailApiService,
  ReviewSubmissionApiService,
  useListPendingSubmissions,
  useGetSubmissionDetail,
  useReviewSubmission,
};
